/**
 * BoothPreferencesForm - Form for vendors to save booth preferences
 * Issue #677 - Vendor Booth Preferences & Smart Matching
 */

import React, { useState, useEffect } from 'react';
import { Save, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { boothPreferencesApi } from '../services/api';
import { VendorBoothPreferences, VendorBoothPreferencesUpdate } from '../types';
import { getErrorMessage } from '../utils/errorUtils';

interface BoothPreferencesFormProps {
  vendorId: number;
  onSaveSuccess?: (preferences: VendorBoothPreferences) => void;
}

export default function BoothPreferencesForm({ vendorId, onSaveSuccess }: BoothPreferencesFormProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState<VendorBoothPreferencesUpdate>({
    prefers_corner: false,
    prefers_high_traffic: false,
    prefers_near_entrance: false,
    requires_power: false,
    prefers_away_from_competitors: false,
    preferred_neighbors: [],
    budget_min: undefined,
    budget_max: undefined,
    min_dimensions: '',
    notes: '',
  });

  // Load existing preferences
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        setLoading(true);
        const preferences = await boothPreferencesApi.getPreferences(vendorId);
        setFormData({
          prefers_corner: preferences.prefers_corner,
          prefers_high_traffic: preferences.prefers_high_traffic,
          prefers_near_entrance: preferences.prefers_near_entrance,
          requires_power: preferences.requires_power,
          prefers_away_from_competitors: preferences.prefers_away_from_competitors,
          preferred_neighbors: preferences.preferred_neighbors || [],
          budget_min: preferences.budget_min ?? undefined,
          budget_max: preferences.budget_max ?? undefined,
          min_dimensions: preferences.min_dimensions || '',
          notes: preferences.notes || '',
        });
      } catch (err: unknown) {
        // If preferences don't exist yet (404), use defaults
        const errorMsg = getErrorMessage(err);
        if (!errorMsg.includes('404') && !errorMsg.includes('not found')) {
          setError(errorMsg);
        }
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, [vendorId]);

  const handleCheckboxChange = (field: keyof VendorBoothPreferencesUpdate) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleInputChange = (field: keyof VendorBoothPreferencesUpdate, value: string | number | undefined | null) => {
    // Convert null to undefined for consistency
    const normalizedValue = value === null ? undefined : value;
    setFormData(prev => ({ ...prev, [field]: normalizedValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    // Validation
    if (typeof formData.budget_min === 'number' && formData.budget_min < 0) {
      setError('Minimum budget cannot be negative');
      return;
    }

    if (typeof formData.budget_max === 'number' && formData.budget_max < 0) {
      setError('Maximum budget cannot be negative');
      return;
    }

    if (
      typeof formData.budget_min === 'number' &&
      typeof formData.budget_max === 'number' &&
      formData.budget_min > formData.budget_max
    ) {
      setError('Minimum budget cannot be greater than maximum budget');
      return;
    }

    try {
      setSaving(true);
      const updated = await boothPreferencesApi.updatePreferences(vendorId, formData);
      setSuccessMessage('Booth preferences saved successfully!');
      if (onSaveSuccess) {
        onSaveSuccess(updated);
      }
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-secondary" />
        <span className="ml-2 text-gray-600">Loading preferences...</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Booth Preferences</h3>
        <p className="text-sm text-gray-600">
          Save your booth preferences to get personalized recommendations for available booths at fairs.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-2">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-800">{successMessage}</p>
        </div>
      )}

      {/* Boolean Preferences */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-900">Location Preferences</h4>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.prefers_corner || false}
            onChange={() => handleCheckboxChange('prefers_corner')}
            className="mt-1 h-4 w-4 text-secondary focus:ring-dusty-teal border-gray-300 rounded"
            aria-label="Corner booth"
            aria-describedby="corner-booth-desc"
            data-testid="boothPreferences-prefersCorner-checkbox"
          />
          <div>
            <div className="text-sm font-medium text-gray-900">Corner booth</div>
            <div id="corner-booth-desc" className="text-xs text-gray-600">Booths at corners have more visibility</div>
          </div>
        </label>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.prefers_high_traffic || false}
            onChange={() => handleCheckboxChange('prefers_high_traffic')}
            className="mt-1 h-4 w-4 text-secondary focus:ring-dusty-teal border-gray-300 rounded"
            aria-label="High traffic area"
            aria-describedby="high-traffic-desc"
            data-testid="boothPreferences-prefersHighTraffic-checkbox"
          />
          <div>
            <div className="text-sm font-medium text-gray-900">High traffic area</div>
            <div id="high-traffic-desc" className="text-xs text-gray-600">Located in busy areas with more foot traffic</div>
          </div>
        </label>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.prefers_near_entrance || false}
            onChange={() => handleCheckboxChange('prefers_near_entrance')}
            className="mt-1 h-4 w-4 text-secondary focus:ring-dusty-teal border-gray-300 rounded"
            aria-label="Near entrance"
            aria-describedby="near-entrance-desc"
            data-testid="boothPreferences-prefersNearEntrance-checkbox"
          />
          <div>
            <div className="text-sm font-medium text-gray-900">Near entrance</div>
            <div id="near-entrance-desc" className="text-xs text-gray-600">Close to main entrance for early visitor traffic</div>
          </div>
        </label>
      </div>

      {/* Requirements */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-900">Requirements</h4>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.requires_power || false}
            onChange={() => handleCheckboxChange('requires_power')}
            className="mt-1 h-4 w-4 text-secondary focus:ring-dusty-teal border-gray-300 rounded"
            aria-label="Requires power outlet"
            aria-describedby="requires-power-desc"
          />
          <div>
            <div className="text-sm font-medium text-gray-900">Requires power outlet</div>
            <div id="requires-power-desc" className="text-xs text-gray-600">Essential for demonstrations or equipment</div>
          </div>
        </label>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.prefers_away_from_competitors || false}
            onChange={() => handleCheckboxChange('prefers_away_from_competitors')}
            className="mt-1 h-4 w-4 text-secondary focus:ring-dusty-teal border-gray-300 rounded"
            aria-label="Away from competitors"
            aria-describedby="away-from-competitors-desc"
          />
          <div>
            <div className="text-sm font-medium text-gray-900">Away from competitors</div>
            <div id="away-from-competitors-desc" className="text-xs text-gray-600">Prefer distance from similar vendors</div>
          </div>
        </label>
      </div>

      {/* Budget Range */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-900">Budget Range</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="budget_min" className="block text-sm text-gray-700 mb-1">
              Minimum ($)
            </label>
            <input
              id="budget_min"
              type="number"
              step="0.01"
              value={formData.budget_min !== undefined ? formData.budget_min : ''}
              onChange={(e) => handleInputChange('budget_min', e.target.value ? parseFloat(e.target.value) : undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusty-teal focus:border-dusty-teal"
              placeholder="50.00"
            />
          </div>
          <div>
            <label htmlFor="budget_max" className="block text-sm text-gray-700 mb-1">
              Maximum ($)
            </label>
            <input
              id="budget_max"
              type="number"
              step="0.01"
              value={formData.budget_max !== undefined ? formData.budget_max : ''}
              onChange={(e) => handleInputChange('budget_max', e.target.value ? parseFloat(e.target.value) : undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusty-teal focus:border-dusty-teal"
              placeholder="200.00"
            />
          </div>
        </div>
      </div>

      {/* Minimum Dimensions */}
      <div>
        <label htmlFor="min_dimensions" className="block text-sm font-medium text-gray-900 mb-1">
          Minimum Dimensions
        </label>
        <input
          id="min_dimensions"
          type="text"
          value={formData.min_dimensions || ''}
          onChange={(e) => handleInputChange('min_dimensions', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusty-teal focus:border-dusty-teal"
          placeholder="e.g., 10x10, 8x12"
        />
        <p className="mt-1 text-xs text-gray-600">Format: width x depth (e.g., 10x10 for a 10ft by 10ft booth)</p>
      </div>

      {/* Notes */}
      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-900 mb-1">
          Additional Notes
        </label>
        <textarea
          id="notes"
          rows={3}
          value={formData.notes || ''}
          onChange={(e) => handleInputChange('notes', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dusty-teal focus:border-dusty-teal"
          placeholder="Any other preferences or requirements..."
          maxLength={500}
        />
        <p className="mt-1 text-xs text-gray-600">{(formData.notes || '').length}/500 characters</p>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-4">
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          data-testid="submit-boothPreferences-button"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Preferences
            </>
          )}
        </button>
      </div>
    </form>
  );
}
